/**
 * Helpers for traversing the NAV_REGISTRY tree.
 *
 * The tree is the canonical hierarchy that drives both the sidebar
 * and the RBAC subject space. Subjects are dotted paths
 * ("ops.crm.customers") and ancestor lookups use simple string ops.
 */
import { NAV_REGISTRY, NAV_ROOTS, type NavNode, type DashboardId } from "./navRegistry";

/** Walk every node depth-first. */
export function walkNodes(roots: NavNode[] = NAV_ROOTS): NavNode[] {
  const out: NavNode[] = [];
  const visit = (n: NavNode) => {
    out.push(n);
    n.children?.forEach(visit);
  };
  roots.forEach(visit);
  return out;
}

let _byId: Map<string, NavNode> | null = null;
function indexById(): Map<string, NavNode> {
  if (_byId) return _byId;
  const idx = new Map<string, NavNode>();
  for (const n of walkNodes()) idx.set(n.id, n);
  _byId = idx;
  return idx;
}

export function getNode(subjectId: string): NavNode | undefined {
  return indexById().get(subjectId);
}

/** Returns ["ops.crm.customers", "ops.crm", "ops", "all"]. */
export function subjectAncestors(subjectId: string): string[] {
  if (!subjectId || subjectId === "all") return ["all"];
  const out: string[] = [];
  const parts = subjectId.split(".");
  for (let i = parts.length; i > 0; i--) out.push(parts.slice(0, i).join("."));
  out.push("all");
  return out;
}

/** All dotted subject ids declared by the registry, plus the "all" wildcard. */
export function getAllSubjects(): string[] {
  return ["all", ...walkNodes().map((n) => n.id)];
}

/** Find the dashboard a subject belongs to (e.g. "ops.crm.customers" → "ops"). */
export function dashboardOfSubject(subjectId: string): DashboardId | null {
  const top = subjectId.split(".")[0];
  return (top in NAV_REGISTRY) ? (top as DashboardId) : null;
}

/** Flatten the registry into matrix rows with depth + parent label for grouping. */
export interface MatrixRow {
  node: NavNode;
  depth: number;
  /** All ancestor ids INCLUDING this node, root first. Used for indent + inheritance. */
  path: string[];
}

export function flattenForMatrix(roots: NavNode[] = NAV_ROOTS): MatrixRow[] {
  const out: MatrixRow[] = [];
  const visit = (n: NavNode, depth: number, path: string[]) => {
    const newPath = [...path, n.id];
    out.push({ node: n, depth, path: newPath });
    n.children?.forEach((c) => visit(c, depth + 1, newPath));
  };
  roots.forEach((r) => visit(r, 0, []));
  return out;
}
